import React__default, { useLayoutEffect, createContext, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useTouchCallbacks, frameDefaultStyle, trackDefaultStyle, slideDefaultStyle, slideInnerStyle } from './utils.js';

var _excluded = ["items", "children", "timer", "slidesToShow", "slidesToScroll", "limitScrollIndex", "loopAround", "adaptiveHeight", "adaptiveHeightLimit", "autoplay", "dragThreshold"];
var useBrowserLayoutEffect = process.browser ? useLayoutEffect : function () {};
var Context = /*#__PURE__*/createContext({});
var Carousel = function Carousel(_ref) {
  var items = _ref.items,
    children = _ref.children,
    _ref$timer = _ref.timer,
    timer = _ref$timer === void 0 ? 4000 : _ref$timer,
    _ref$slidesToShow = _ref.slidesToShow,
    slidesToShow = _ref$slidesToShow === void 0 ? 1 : _ref$slidesToShow,
    _ref$slidesToScroll = _ref.slidesToScroll,
    slidesToScroll = _ref$slidesToScroll === void 0 ? 1 : _ref$slidesToScroll,
    _ref$limitScrollIndex = _ref.limitScrollIndex,
    limitScrollIndex = _ref$limitScrollIndex === void 0 ? true : _ref$limitScrollIndex,
    _ref$loopAround = _ref.loopAround,
    loopAround = _ref$loopAround === void 0 ? true : _ref$loopAround,
    _ref$adaptiveHeight = _ref.adaptiveHeight,
    adaptiveHeight = _ref$adaptiveHeight === void 0 ? false : _ref$adaptiveHeight,
    _ref$adaptiveHeightLi = _ref.adaptiveHeightLimit,
    adaptiveHeightLimit = _ref$adaptiveHeightLi === void 0 ? 500 : _ref$adaptiveHeightLi,
    _ref$autoplay = _ref.autoplay,
    autoplay = _ref$autoplay === void 0 ? false : _ref$autoplay,
    _ref$dragThreshold = _ref.dragThreshold,
    dragThreshold = _ref$dragThreshold === void 0 ? 80 : _ref$dragThreshold,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _useState = useState(0),
    wrapperWidth = _useState[0],
    setWrapperWidth = _useState[1];
  var _useState2 = useState(0),
    slideHeight = _useState2[0],
    setSlideHeight = _useState2[1];
  var _useState3 = useState(false),
    stopTouch = _useState3[0],
    setStopTouch = _useState3[1];
  var _useState4 = useState(0),
    current = _useState4[0],
    setCurrent = _useState4[1];
  var _useState5 = useState(autoplay),
    isAutoplaying = _useState5[0],
    setAutoplaying = _useState5[1];
  var el = useRef(null);
  var slideEl = useRef(null);
  var slidesEl = useRef(null);
  var itemsLength = items.length;
  var context = useMemo(function () {
    var mapCurrent = function mapCurrent(fn) {
      var reqIndex = fn(current, itemsLength);
      var nextIndex = loopAround ? reqIndex % itemsLength : Math.max(0, Math.min(itemsLength - 1, reqIndex));
      if (limitScrollIndex) {
        nextIndex = nextIndex < 0 ? Math.ceil((itemsLength + nextIndex) / slidesToScroll) * slidesToScroll : Math.floor(nextIndex / slidesToScroll) * slidesToScroll;
      } else if (nextIndex < 0) {
        nextIndex = itemsLength + nextIndex;
      }
      setCurrent(nextIndex);
      return nextIndex;
    };
    return {
      current: current,
      mapCurrent: mapCurrent,
      setCurrent: function setCurrent(index) {
        return mapCurrent(function () {
          return index;
        });
      },
      next: function next() {
        return mapCurrent(function (next) {
          return next + slidesToScroll;
        });
      },
      prev: function prev() {
        return mapCurrent(function (next) {
          return next - slidesToScroll;
        });
      },
      numItems: itemsLength,
      slidesToScroll: slidesToScroll
    };
  }, [current, itemsLength, setCurrent, loopAround, slidesToScroll, limitScrollIndex]);
  var _useTouchCallbacks = useTouchCallbacks({
      onTouchStart: function onTouchStart() {
        setStopTouch(true);
        setAutoplaying(false);
      },
      onTouchEnd: useCallback(function (start, current) {
        if (current.x > start.x + dragThreshold) {
          context.prev();
        } else if (current.x < start.x - dragThreshold) {
          context.next();
        }
        if (autoplay) {
          setAutoplaying(true);
        }
        setStopTouch(false);
      }, [autoplay, context, dragThreshold, setAutoplaying])
    }),
    resetTouch = _useTouchCallbacks.resetTouch,
    touchStart = _useTouchCallbacks.touchStart,
    touchCurrent = _useTouchCallbacks.touchCurrent,
    touchCallbacks = _useTouchCallbacks.touchCallbacks;

  // Reset touch-start when current is no longer the same
  useEffect(function () {
    return resetTouch;
  }, [current, resetTouch]);
  if (process.browser) {
    // We only calculate sizes which depend on client here
    /* eslint-disable react-hooks/rules-of-hooks */
    useBrowserLayoutEffect(function () {
      var calculateWrapperSize = function calculateWrapperSize() {
        return setWrapperWidth(el.current ? el.current.offsetWidth : 0);
      };
      window.addEventListener("resize", calculateWrapperSize);
      calculateWrapperSize();
      return function () {
        return window.removeEventListener("resize", calculateWrapperSize);
      };
    }, [el, setWrapperWidth]);
    useBrowserLayoutEffect(function () {
      var childArr = slidesEl.current ? Array.from(slidesEl.current.children) : [];
      var calcChildrenHighestOffestHeight = function calcChildrenHighestOffestHeight() {
        var highestOffsetHeight = 0;
        childArr.forEach(function (child) {
          var childContent = child.children[0];
          if (childContent && childContent.offsetHeight > highestOffsetHeight) {
            highestOffsetHeight = childContent.offsetHeight;
          }
        });
        if (highestOffsetHeight > adaptiveHeightLimit) {
          highestOffsetHeight = adaptiveHeightLimit;
        }
        setSlideHeight(highestOffsetHeight);
      };
      window.addEventListener("resize", calcChildrenHighestOffestHeight);
      setTimeout(calcChildrenHighestOffestHeight, 500);
      return function () {
        return window.removeEventListener("resize", calcChildrenHighestOffestHeight);
      };
    }, [slideEl, slidesEl, setSlideHeight]);

    /* eslint-enable react-hooks/rules-of-hooks */
  }

  useEffect(function () {
    if (isAutoplaying) {
      var autoplayTimer = setTimeout(context.next, timer);
      return function () {
        return clearTimeout(autoplayTimer);
      };
    }
  }, [isAutoplaying, context, timer]);
  var dragDelta = touchCurrent && touchStart ? touchCurrent.x - touchStart.x : 0;
  var currentSlide = Math.max(0, Math.min(itemsLength <= 0 ? 0 : itemsLength - 1, current));
  var slideWidth = wrapperWidth / slidesToShow;
  var trackWidth = slideWidth * items.length;
  var frameWidth = Math.min(slideWidth * slidesToShow, wrapperWidth);
  var styleData = {
    currentSlide: currentSlide,
    dragDelta: dragDelta,
    slideHeight: slideHeight,
    slideWidth: slideWidth,
    trackWidth: trackWidth,
    frameWidth: frameWidth
  };
  return /*#__PURE__*/React__default.createElement(Context.Provider, {
    value: context
  }, /*#__PURE__*/React__default.createElement("div", _extends({}, props, touchCallbacks, {
    ref: el
  }), /*#__PURE__*/React__default.createElement("div", {
    style: frameDefaultStyle(styleData)
  }, /*#__PURE__*/React__default.createElement("div", {
    ref: slidesEl,
    style: _extends({}, trackDefaultStyle(styleData), {
      touchAction: stopTouch ? "none" : "auto"
    })
  }, items.map(function (item, i) {
    return /*#__PURE__*/React__default.createElement("div", {
      key: i,
      ref: slideEl,
      style: slideDefaultStyle(styleData)
    }, /*#__PURE__*/React__default.createElement("div", {
      style: adaptiveHeight ? slideInnerStyle(styleData) : null
    }, item));
  }))), children));
};

export { Carousel, Context };
//# sourceMappingURL=index.js.map
